import { ErrorAlert, SelectInput, TextInput, useDebouncedValue } from '@lumlab/platform-ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { apiRequest } from '@lumlab/platform-product/web';
import type { DirectoryOrganization, DirectoryPage } from '../contract/index.ts';

/** Search is server-side; the URL retains the selected organization across product screens. */
export function OrganizationScope({ children }: { children: (organizationId: string) => ReactNode }) {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const selected = params.get('organizationId');
    const debouncedSearch = useDebouncedValue(search, 250);
    const organizations = useQuery({
        queryKey: ['admin', 'organization-picker', debouncedSearch],
        queryFn: ({ signal }) =>
            apiRequest<DirectoryPage<DirectoryOrganization>>(
                `/api/admin/organizations?${new URLSearchParams({ search: debouncedSearch, pageSize: '100' })}`,
                { signal },
            ),
    });
    const detail = useQuery({
        queryKey: ['admin', 'organization', selected],
        enabled: Boolean(selected),
        retry: false,
        queryFn: ({ signal }) => apiRequest<DirectoryOrganization>(`/api/admin/organizations/${selected}`, { signal }),
    });
    const options = organizations.data?.items.map((item) => ({ value: item.id, label: item.name })) ?? [];
    if (detail.data && !options.some((item) => item.value === detail.data.id))
        options.push({ value: detail.data.id, label: detail.data.name });
    const missingSelection = selected && !options.some((option) => option.value === selected);
    return (
        <div className="flex flex-col gap-4 mb-6">
            <TextInput
                type="search"
                label={t('platform.admin.chooseOrganization')}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <SelectInput
                label={t('platform.admin.organization')}
                value={selected ?? ''}
                aria-busy={Boolean(selected) && detail.isPending}
                onChange={(event) => {
                    const value = event.currentTarget.value;
                    const next = new URLSearchParams(params);
                    if (value) next.set('organizationId', value);
                    else next.delete('organizationId');
                    setParams(next);
                }}
            >
                <option value="">{t('platform.admin.selectOrganization')}</option>
                {missingSelection && (
                    <option value={selected} disabled>
                        {detail.isPending ? t('platform.shell.loading') : t('platform.admin.organizationUnavailable')}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </SelectInput>
            {!organizations.isPending && !organizations.error && options.length === 0 && (
                <p className="text-sm text-muted" role="status">
                    {t('platform.directory.empty')}
                </p>
            )}
            {organizations.error && <ErrorAlert error={organizations.error} />}
            {detail.error ? (
                <ErrorAlert error={detail.error} />
            ) : selected && detail.isPending ? (
                <p role="status" className="text-sm text-muted">
                    {t('platform.shell.loading')}
                </p>
            ) : selected && detail.data ? (
                <div className="flex flex-col gap-4" key={selected}>
                    {children(selected)}
                </div>
            ) : (
                <p className="text-muted">{t('platform.admin.chooseOrganizationHelp')}</p>
            )}
        </div>
    );
}
